import { forwardRef, Ref, useRef } from "react";

import { CodeNode } from "@lexical/code";
import { $generateHtmlFromNodes } from "@lexical/html";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListNode, ListItemNode } from "@lexical/list";
import { TRANSFORMERS } from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { Box, styled, SxProps, Theme } from "@mui/material";
import { EditorState, LexicalEditor } from "lexical";

import { DefaultValuePlugin } from "./DefaultValuePlugin";
import { FloatingToolbarPlugin } from "./FloatingToolbarPlugin";
import { $isEmpty, serialize } from "./helpers";

import { mergeSx } from "@/helpers/mergeSx";
import { mergeRefs } from "@/helpers/refs";

const MATCHERS = [
  (text: string) => {
    const match =
      /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/.exec(
        text
      );

    if (match === null) {
      return null;
    }

    const fullMatch = match[0];

    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith("http") ? fullMatch : `https://${fullMatch}`,
      attributes: { rel: "noreferrer", target: "_blank" },
    };
  },
];

export const getRichTextInputTextStyles = (theme: Theme) => ({
  ["& p"]: {
    margin: 0,
  },
  ["& .lxcl-bold"]: {
    fontWeight: theme.typography.fontWeightBold,
  },
  ["& .lxcl-italic"]: {
    fontStyle: "italic",
  },
  ["& .lxcl-underline"]: {
    textDecoration: "underline",
  },
});

export const getRichTextInputLinkStyles = (linkColor: string) => ({
  ["& .lxcl-link"]: {
    color: `${linkColor} !important`,
  },
});

const StyledContentEditable = styled(ContentEditable)(({ theme }) => ({
  position: "relative",
  minHeight: 80,
  padding: theme.spacing(1),
  ...theme.typography.body2,
  backgroundColor: theme.palette.surface.neutral.white,
  border: `1px solid ${theme.palette.border.neutral.quiet}`,
  borderRadius: theme.radius(1),
  outlineColor: "transparent",
  transition: "all 50ms ease-out, outline 100ms ease-out",
  "&:focus": {
    border: `1px solid ${theme.palette.border.brand.intense}`,
    outline: `3px solid ${theme.palette.border.brand.moderate}`,
  },
  ...getRichTextInputTextStyles(theme),
  ["& > *"]: {
    zIndex: 2, // Position above the placeholder
  },
}));

type RichTextInputPlaceholderProps = {
  placeholder: string;
};

const RichTextInputPlaceholder = ({ placeholder }: RichTextInputPlaceholderProps) => {
  return (
    <Box
      component="span"
      sx={(theme) => ({
        position: "absolute",
        zIndex: 1,
        top: 8,
        left: 8,
        ...theme.typography.body2,
        color: theme.palette.text.form.supershy,
        userSelect: "none",
        pointerEvents: "none",
      })}
    >
      {placeholder}
    </Box>
  );
};

export type RichTextInputProps = {
  inputRef?: Ref<HTMLDivElement>;
  value?: string | null;
  defaultValue?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  sx?: SxProps<Theme>;
  error?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

export const DashboardRichTextInput = forwardRef<HTMLDivElement, RichTextInputProps>(
  ({ inputRef, defaultValue, placeholder, onChange, error, sx, autoFocus = false, disabled = false }, ref) => {
    const initialConfig = {
      namespace: "lexical-editor",
      editable: !disabled,
      nodes: [HeadingNode, QuoteNode, LinkNode, AutoLinkNode, ListNode, ListItemNode, CodeNode, HorizontalRuleNode],
      theme: {
        ltr: "lxcl-ltr",
        rtl: "lxcl-rtl",
        paragraph: "lxcl-paragraph",
        quote: "lxcl-quote",
        heading: {
          h1: "lxcl-h1",
          h2: "lxcl-h2",
          h3: "lxcl-h3",
          h4: "lxcl-h4",
          h5: "lxcl-h5",
          h6: "lxcl-h6",
        },
        list: {
          nested: {
            listitem: "lxcl-nested-listitem",
          },
          ol: "lxcl-ol",
          ul: "lxcl-ul",
          listitem: "lxcl-listItem",
          listitemChecked: "lxcl-listItemChecked",
          listitemUnchecked: "lxcl-listItemUnchecked",
        },
        hashtag: "lxcl-hashtag",
        image: "lxcl-image",
        link: "lxcl-link",
        text: {
          bold: "lxcl-bold",
          code: "lxcl-code",
          italic: "lxcl-italic",
          strikethrough: "lxcl-strikethrough",
          subscript: "lxcl-subscript",
          superscript: "lxcl-superscript",
          underline: "lxcl-underline",
          underlineStrikethrough: "lxcl-underlineStrikethrough",
        },
      },
      onError: (error: Error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
    };

    const contentEditableRef = useRef<HTMLDivElement>(null);
    const outerRef = mergeRefs(ref, inputRef);

    const handleOnChange = (editorState: EditorState, editor: LexicalEditor) => {
      if (onChange) {
        editorState.read(() => {
          if ($isEmpty()) {
            onChange("");
            return;
          }

          const html = $generateHtmlFromNodes(editor, null);
          onChange(serialize(html));
        });
      }
    };

    const styles = mergeSx(
      {
        ...(error && {
          borderColor: (theme) => theme.palette.border.danger.intense,
        }),
        ...(disabled && {
          opacity: 0.4,
        }),
      },
      sx
    );

    return (
      <Box
        ref={outerRef}
        tabIndex={0}
        onFocus={() => contentEditableRef.current?.focus()} // This is a hack to prevent the content editable from stealing focus from other inputs in a page
        sx={{ position: "relative" }}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <RichTextPlugin
            contentEditable={
              <StyledContentEditable
                ref={contentEditableRef}
                placeholder={placeholder ? <RichTextInputPlaceholder placeholder={placeholder} /> : <div />}
                aria-placeholder={placeholder ?? ""}
                sx={styles}
                disabled={disabled}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <AutoLinkPlugin matchers={MATCHERS} />
          <TabIndentationPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <HistoryPlugin />
          <FloatingToolbarPlugin />
          <OnChangePlugin onChange={handleOnChange} />
          <DefaultValuePlugin defaultValue={defaultValue} />
          {autoFocus && <AutoFocusPlugin />}
        </LexicalComposer>
      </Box>
    );
  }
);

DashboardRichTextInput.displayName = "DashboardRichTextInput";
